import React, { useState } from 'react';
import './StockHistoryModal.css';

const StockHistoryModal = ({ product, onClose }) => {
  const [selectedProduct, setSelectedProduct] = useState(product?.name || '');
  const [location, setLocation] = useState('All Locations');

  // Multi-location or single selection placeholder
  const businessLocations = ['All Locations', 'Main Warehouse', 'Downtown Store', 'Uptown Branch'];

  // Mock data for the demonstration based on requested fields
  const stats = {
    in: {
      totalPurchase: '150.00',
      openingStock: '50.00',
      totalSellReturn: '5.00',
      stockTransfersIn: '20.00',
    },
    out: {
      totalSold: '120.00',
      totalStockAdjustment: '2.00',
      totalPurchaseReturn: '10.00',
      stockTransfersOut: '15.00',
    },
    totals: {
      currentStock: '78.00'
    }
  };

  const historyData = [
    { type: 'Purchase', change: '+100.00', newQty: '150.00', date: '2026-04-10 14:30', refNo: 'PO-2026-001', info: 'Supplier: Tech Corp' },
    { type: 'Sell', change: '-20.00', newQty: '130.00', date: '2026-04-11 09:15', refNo: 'INV-10023', info: 'Customer: John Doe' },
    { type: 'Adjustment', change: '-2.00', newQty: '128.00', date: '2026-04-11 11:00', refNo: 'ADJ-501', info: 'Reason: Damaged' },
    { type: 'Transfer', change: '+20.00', newQty: '148.00', date: '2026-04-11 16:45', refNo: 'TRF-302', info: 'From: Central Warehouse' },
    { type: 'Sell', change: '-70.00', newQty: '78.00', date: '2026-04-11 18:20', refNo: 'INV-10055', info: 'Customer: Walk-in' },
  ];

  if (!product) return null;

  return (
    <div className="stock-history-modal-overlay" onClick={onClose}>
      <div className="stock-history-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="stock-history-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2>Product Stock History</h2>
            <span className="product-ref-tag">#{product.sku || product.id}</span>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="stock-history-body">
          {/* Header Info */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: '800', margin: '0 0 0.5rem 0', color: '#0f172a' }}>
              {product.name}
            </h1>
          </div>

          {/* Filters */}
          <div className="stock-history-filters">
            <div className="filter-group">
              <label>Product</label>
              <select className="filter-select" value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
                <option>{product.name}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Business Location</label>
              <select className="filter-select" value={location} onChange={(e) => setLocation(e.target.value)}>
                {businessLocations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Statistics Grid */}
          <div className="stock-history-stats">
            {/* Column 1: Quantities In */}
            <div className="stats-column column-in">
              <h3><i className="fa fa-arrow-circle-down"></i> Quantities In</h3>
              <div className="stat-item">
                <span className="stat-label">Total Purchase</span>
                <span className="stat-value">{stats.in.totalPurchase}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Opening Stock</span>
                <span className="stat-value">{stats.in.openingStock}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Sell Return</span>
                <span className="stat-value">{stats.in.totalSellReturn}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Stock Transfers (In)</span>
                <span className="stat-value">{stats.in.stockTransfersIn}</span>
              </div>
            </div>

            {/* Column 2: Quantities Out */}
            <div className="stats-column column-out">
              <h3><i className="fa fa-arrow-circle-up"></i> Quantities Out</h3>
              <div className="stat-item">
                <span className="stat-label">Total Sold</span>
                <span className="stat-value">{stats.out.totalSold}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Stock Adjustment</span>
                <span className="stat-value">{stats.out.totalStockAdjustment}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Purchase Return</span>
                <span className="stat-value">{stats.out.totalPurchaseReturn}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Stock Transfers (Out)</span>
                <span className="stat-value">{stats.out.stockTransfersOut}</span>
              </div>
            </div>

            {/* Column 3: Totals */}
            <div className="stats-column column-totals">
              <h3><i className="fa fa-calculator"></i> Totals</h3>
              <div className="stat-item total-highlight">
                <span className="stat-label">Current stock</span>
                <span className="stat-value">{stats.totals.currentStock}</span>
              </div>
            </div>
          </div>

          {/* History Table */}
          <div className="history-table-section">
            <h3>Stock History Log</h3>
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Quantity Change</th>
                    <th>New Quantity</th>
                    <th>Date</th>
                    <th>Reference No</th>
                    <th>Customer/Supplier Info</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <span className={`badge-type ${row.type.toLowerCase()}`}>
                          {row.type}
                        </span>
                      </td>
                      <td className={`stock-change ${row.change.startsWith('+') ? 'positive' : 'negative'}`}>
                        {row.change}
                      </td>
                      <td>{row.newQty}</td>
                      <td>{row.date}</td>
                      <td style={{ fontWeight: '600' }}>{row.refNo}</td>
                      <td style={{ color: '#64748b' }}>{row.info}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockHistoryModal;
